import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Send, CheckCircle2 } from 'lucide-react';

export default function Contact() {
  const [status, setStatus] = useState('idle'); // idle, loading, success, error

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');

    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setStatus('success');
        e.target.reset();
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  return (
    <>
      <Helmet>
        <title>Kontakt MJ Detailing | Få et Uforpligtende Tilbud Nu</title>
        <meta name="description" content="Klar til at give din bil nyt liv? Kontakt MJ Detailing i Billund for et uforpligtende tilbud på bilpleje, polering eller coating. Ring eller skriv i dag!" />
      </Helmet>

      <section className="pt-40 pb-24 bg-black">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-20">
              {/* Info */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-12"
              >
                <div>
                  <span className="text-zinc-500 font-bold tracking-widest uppercase text-sm mb-4 block">Kontakt Os</span>
                  <h1 className="text-4xl md:text-5xl font-black tracking-normal mb-8 leading-tight uppercase">
                    SKAL VI <br />
                    <span className="bg-linear-to-r from-zinc-500 via-white to-zinc-500 bg-[length:200%_auto] bg-clip-text text-transparent animate-shine">SNAKKE BIL?</span>
                  </h1>
                  <p className="text-zinc-400 text-lg leading-relaxed">
                    Udfyld formularen, ring eller send en mail. Vi svarer hurtigst muligt og giver dig et uforpligtende tilbud på din opgave.
                  </p>
                </div>

                <div className="space-y-6">
                  <a href="tel:+4527895995" className="flex items-center gap-6 group">
                    <div className="w-14 h-14 rounded-2xl bg-white/5 flex items-center justify-center border border-white/10 group-hover:bg-white group-hover:text-black transition-all">
                      <Phone size={24} />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1">Ring til os</p>
                      <p className="text-xl font-bold">+45 27 89 59 95</p>
                    </div>
                  </a>
                  <a href="mailto:mj.detailing.7190@gmail.com" className="flex items-center gap-6 group">
                    <div className="w-14 h-14 rounded-2xl bg-white/5 flex items-center justify-center border border-white/10 group-hover:bg-white group-hover:text-black transition-all">
                      <Mail size={24} />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1">Send en mail</p>
                      <p className="text-xl font-bold">mj.detailing.7190@gmail.com</p>
                    </div>
                  </a>
                  <div className="flex items-center gap-6 group">
                    <div className="w-14 h-14 rounded-2xl bg-white/5 flex items-center justify-center border border-white/10">
                      <MapPin size={24} />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1">Find os</p>
                      <p className="text-xl font-bold">Grindstedvej 10, 7190 Billund</p>
                    </div>
                  </div>
                </div>

                <div className="pt-8 border-t border-white/10">
                  <p className="text-sm text-zinc-500">CVR: 45818608</p>
                </div>
              </motion.div>

              {/* Form */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-zinc-900 border border-white/10 rounded-3xl p-8 md:p-12"
              >
                {status === 'success' ? (
                  <div className="h-full flex flex-col items-center justify-center text-center space-y-6 py-12">
                    <div className="w-20 h-20 bg-white text-black rounded-full flex items-center justify-center">
                      <CheckCircle2 size={40} />
                    </div>
                    <h3 className="text-3xl font-black tracking-normal uppercase">Besked Sendt!</h3>
                    <p className="text-zinc-400">Tak for din henvendelse. Vi vender tilbage til dig hurtigst muligt.</p>
                    <button
                      onClick={() => setStatus('idle')}
                      className="text-white font-bold border-b border-white pb-1"
                    >
                      Send en ny besked
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Honeypot */}
                    <div className="sr-only absolute -z-10 opacity-0 pointer-events-none">
                      <label htmlFor="website_url">Website URL</label>
                      <input type="text" name="website_url" id="website_url" tabIndex="-1" autoComplete="off" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-xs font-bold uppercase tracking-widest text-zinc-500 ml-1">Navn</label>
                        <input
                          required
                          type="text"
                          name="name"
                          placeholder="Dit navn"
                          className="w-full bg-black border border-white/10 rounded-xl px-4 py-4 text-white focus:outline-none focus:border-white transition-colors"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-bold uppercase tracking-widest text-zinc-500 ml-1">Telefon</label>
                        <input
                          required
                          type="tel"
                          name="phone"
                          placeholder="Dit nummer"
                          className="w-full bg-black border border-white/10 rounded-xl px-4 py-4 text-white focus:outline-none focus:border-white transition-colors"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase tracking-widest text-zinc-500 ml-1">Email</label>
                      <input
                        required
                        type="email"
                        name="email"
                        placeholder="Din email"
                        className="w-full bg-black border border-white/10 rounded-xl px-4 py-4 text-white focus:outline-none focus:border-white transition-colors"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase tracking-widest text-zinc-500 ml-1">Ydelse</label>
                      <select
                        name="service"
                        className="w-full bg-black border border-white/10 rounded-xl px-4 py-4 text-white focus:outline-none focus:border-white transition-colors appearance-none"
                      >
                        <option value="rengoering">Rengøring</option>
                        <option value="polering">Polering</option>
                        <option value="coating">Keramisk Coating</option>
                        <option value="andet">Andet / Ved ikke</option>
                      </select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase tracking-widest text-zinc-500 ml-1">Besked</label>
                      <textarea
                        required
                        name="message"
                        rows="4"
                        placeholder="Fortæl os om din bil og hvad du ønsker udført..."
                        className="w-full bg-black border border-white/10 rounded-xl px-4 py-4 text-white focus:outline-none focus:border-white transition-colors resize-none"
                      ></textarea>
                    </div>

                    <button
                      disabled={status === 'loading'}
                      type="submit"
                      className="w-full bg-white text-black py-4 rounded-xl font-bold uppercase tracking-widest flex items-center justify-center gap-3 hover:bg-zinc-200 transition-all disabled:opacity-50"
                    >
                      {status === 'loading' ? (
                        <div className="w-6 h-6 border-2 border-black/20 border-t-black rounded-full animate-spin" />
                      ) : (
                        <>
                          <Send size={20} /> Send Henvendelse
                        </>
                      )}
                    </button>

                    {status === 'error' && (
                      <p className="text-red-500 text-sm text-center font-bold">Der skete en fejl. Prøv igen eller ring til os.</p>
                    )}
                  </form>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Placeholder */}
      <section className="h-[400px] bg-zinc-900 grayscale">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2254.9123456789!2d9.1234567!3d55.7321!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x464b777777777777%3A0x7777777777777777!2sGrindstedvej%2010%2C%207190%20Billund!5e0!3m2!1sda!2sdk!4v1234567890"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        ></iframe>
      </section>
    </>
  );
}
